// ─── Types ────────────────────────────────────────────────────────────────────

export type AthleteId = string;
export type BoulderClassId = string;
export type BoulderId = string;

export interface BoulderAthlete {
  id: AthleteId;
  name: string;
  gender: 'male' | 'female';
  classId: BoulderClassId;       // merged competition group ID
  paraclimbingClass?: string;    // individual class for display (e.g. 'B1', 'RP3')
}

export interface Boulder {
  id: BoulderId;
  number: number;
}

export interface BoulderSportClass {
  id: string;
  name: string;
}

export interface BoulderClass {
  id: BoulderClassId;
  name: string;
  classIds?: string[];    // which sport class ids belong to this merging group
  gender?: 'men' | 'women'; // if set, only athletes of this gender are ranked in this group
  boulderIds: BoulderId[];
}

export interface BoulderResult {
  athleteId: AthleteId;
  boulderId: BoulderId;
  attempts: number;
  zoned: boolean;
  topped: boolean;
}

export interface BoulderSetup {
  boulders: Boulder[];
  sportClasses: BoulderSportClass[];   // individual paraclimbing classes
  classes: BoulderClass[];             // merging groups
  locked: boolean;
}

export interface BoulderCompetitionData {
  setup: BoulderSetup;
  athletes: BoulderAthlete[];
  /** Keyed by `${athleteId}_${boulderId}` for atomic per-entry Firestore updates */
  results: Record<string, BoulderResult>;
}

export interface AthleteRankRow {
  athlete: BoulderAthlete;
  scores: Record<BoulderId, number>;
  total: number;
  rank: number;
}

// ─── Default athletes (boulder registrants from xlsx) ────────────────────────

export const DEFAULT_BOULDER_ATHLETES: BoulderAthlete[] = [
  // ── Male ──────────────────────────────────────────────────────────────────
  { id: 'bm1',  name: 'Matej Arh',            gender: 'male',   classId: 'AL1'   },
  { id: 'bm2',  name: 'Frederik Leys',         gender: 'male',   classId: 'AL2'   },
  { id: 'bm3',  name: 'David Kammerer',        gender: 'male',   classId: 'AL2'   },
  { id: 'bm4',  name: 'Niek Geerts',           gender: 'male',   classId: 'AL2'   },
  { id: 'bm5',  name: 'Matthew Styles-West',   gender: 'male',   classId: 'AU3'   },
  { id: 'bm6',  name: 'Aldrik Bethke',         gender: 'male',   classId: 'AU3'   },
  { id: 'bm7',  name: 'Maurice Sleuwaegen',    gender: 'male',   classId: 'B2'    },
  { id: 'bm8',  name: 'Tom Callewaert',        gender: 'male',   classId: 'B2'    },
  { id: 'bm9',  name: 'Kevin Leveque',         gender: 'male',   classId: 'B3'    },
  { id: 'bm10', name: 'Mario Persing',         gender: 'male',   classId: 'RP1'   },
  { id: 'bm11', name: 'Andries Hille Oudijk',  gender: 'male',   classId: 'RP1'   },
  { id: 'bm12', name: 'Paul Martin',           gender: 'male',   classId: 'RP1'   },
  { id: 'bm13', name: 'Jamie Barendrecht',     gender: 'male',   classId: 'RP3-M' },
  { id: 'bm14', name: 'Jannes Thies Gick',     gender: 'male',   classId: 'RP3-M' },
  // ── Female ────────────────────────────────────────────────────────────────
  { id: 'bf1',  name: 'Sarah Larcombe',        gender: 'female', classId: 'AL2'   },
  { id: 'bf3',  name: 'Manca Smrekar',         gender: 'female', classId: 'AU3'   },
  { id: 'bf4',  name: 'Tanja Glušič',          gender: 'female', classId: 'B1'    },
  { id: 'bf6',  name: 'Linn Poston',           gender: 'female', classId: 'B3'    },
  { id: 'bf7',  name: 'Melissa Ruiz',          gender: 'female', classId: 'RP1'   },
  { id: 'bf8',  name: 'Anita Aggarwal',        gender: 'female', classId: 'RP3-F' },
  { id: 'bf9',  name: 'Crystal Martin',        gender: 'female', classId: 'RP3-F' },
  { id: 'bf10', name: 'Tatjana Nußer',         gender: 'female', classId: 'RP3-F' },
  { id: 'bf11', name: 'Beki Roberts',          gender: 'female', classId: 'RP3-F' },
  { id: 'bf12', name: 'Nat Vorel',             gender: 'female', classId: 'RP3-F' },
];

export const DEFAULT_BOULDER_SPORT_CLASSES: BoulderSportClass[] = [
  { id: 'AL1', name: 'AL1' }, { id: 'AL2', name: 'AL2' },
  { id: 'AU2', name: 'AU2' }, { id: 'AU3', name: 'AU3' },
  { id: 'B1',  name: 'B1'  }, { id: 'B2',  name: 'B2'  }, { id: 'B3',  name: 'B3'  },
  { id: 'RP1', name: 'RP1' }, { id: 'RP2', name: 'RP2' }, { id: 'RP3', name: 'RP3' },
];

export const DEFAULT_BOULDER_CLASSES: BoulderClass[] = [];

export function getDefaultData(): BoulderCompetitionData {
  return {
    setup: {
      boulders: [],
      sportClasses: DEFAULT_BOULDER_SPORT_CLASSES.map(sc => ({ ...sc })),
      classes: DEFAULT_BOULDER_CLASSES.map(c => ({ ...c, boulderIds: [] as BoulderId[] })),
      locked: false,
    },
    athletes: DEFAULT_BOULDER_ATHLETES.map(a => ({ ...a })),
    results: {},
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
}

export function resultKey(athleteId: AthleteId, boulderId: BoulderId): string {
  return `${athleteId}_${boulderId}`;
}

export function getResult(
  results: Record<string, BoulderResult>,
  athleteId: AthleteId,
  boulderId: BoulderId,
): BoulderResult | undefined {
  return results[resultKey(athleteId, boulderId)];
}

export function setResult(
  results: Record<string, BoulderResult>,
  athleteId: AthleteId,
  boulderId: BoulderId,
  patch: Partial<Omit<BoulderResult, 'athleteId' | 'boulderId'>>,
): Record<string, BoulderResult> {
  const key = resultKey(athleteId, boulderId);
  const current: BoulderResult = results[key] ?? { athleteId, boulderId, attempts: 0, zoned: false, topped: false };
  return { ...results, [key]: { ...current, ...patch } };
}

export function mergeResult(
  results: Record<string, BoulderResult>,
  athleteId: AthleteId,
  boulderId: BoulderId,
  patch: Partial<Omit<BoulderResult, 'athleteId' | 'boulderId'>>,
): BoulderResult {
  const key = resultKey(athleteId, boulderId);
  const current: BoulderResult = results[key] ?? { athleteId, boulderId, attempts: 0, zoned: false, topped: false };
  return { ...current, ...patch };
}

// ─── Scoring ──────────────────────────────────────────────────────────────────

/**
 * Score for a single boulder attempt.
 * - Topped : 10 pts base, -0.1 per attempt, floor 0
 * - Zoned  :  4 pts base, -0.1 per attempt, floor 0
 * - Neither: 0
 */
export function scoreBoulder(result: BoulderResult): number {
  if (result.topped) return Math.max(0, 10 - result.attempts * 0.1);
  if (result.zoned)  return Math.max(0, 4  - result.attempts * 0.1);
  return 0;
}

export function formatScore(score: number): string {
  if (score === 0) return '—';
  return score % 1 === 0 ? score.toFixed(0) : score.toFixed(1);
}

// ─── Rankings ─────────────────────────────────────────────────────────────────

/**
 * Returns ranked groups (explicit merging groups + virtual solo groups for unmerged sport classes).
 * Virtual groups use all configured boulders.
 */
export function computeClassRankings(
  data: BoulderCompetitionData,
): Array<{ cls: BoulderClass; rows: AthleteRankRow[] }> {
  const result: Array<{ cls: BoulderClass; rows: AthleteRankRow[] }> = [];
  const allBoulderIds = data.setup.boulders.map(b => b.id);

  // Track which athletes are covered by an explicit merging group
  const coveredAthleteIds = new Set<string>();

  data.setup.classes.forEach(cls => {
    // New format: match athletes by sport class membership; old format: direct classId match
    const groupClassIds = cls.classIds ?? [];
    const athletes = groupClassIds.length > 0
      ? data.athletes.filter(a => {
          if (!groupClassIds.includes(a.classId)) return false;
          if (cls.gender) return a.gender === (cls.gender === 'men' ? 'male' : 'female');
          return true;
        })
      : data.athletes.filter(a => a.classId === cls.id);
    athletes.forEach(a => coveredAthleteIds.add(a.id));
    if (athletes.length === 0) return;

    const rows: AthleteRankRow[] = athletes.map(athlete => {
      const scores: Record<BoulderId, number> = {};
      let total = 0;
      cls.boulderIds.forEach(bid => {
        const r = data.results[resultKey(athlete.id, bid)];
        const s = r ? scoreBoulder(r) : 0;
        scores[bid] = s;
        total += s;
      });
      return { athlete, scores, total, rank: 0 };
    });

    rows.sort((a, b) => b.total - a.total);
    rows.forEach((r, i) => (r.rank = i + 1));
    result.push({ cls, rows });
  });

  // Athletes not covered by any explicit merging group: virtual group per (classId, gender)
  const uncovered = data.athletes.filter(a => !coveredAthleteIds.has(a.id));
  const uncoveredClassIds = Array.from(new Set(uncovered.map(a => a.classId)));
  uncoveredClassIds.forEach(classId => {
      const classAthletes = uncovered.filter(a => a.classId === classId);
      const className = data.setup.sportClasses.find(sc => sc.id === classId)?.name ?? classId;
      (['male', 'female'] as const).forEach(gender => {
        const athletes = classAthletes.filter(a => a.gender === gender);
        if (athletes.length === 0) return;
        const genderLabel = gender === 'male' ? 'Men' : 'Women';
        const name = `${className} ${genderLabel}`;
        const virtualId = `__solo_${classId}_${gender}`;
        const virtualCls: BoulderClass = {
          id: virtualId, name, classIds: [classId],
          gender: gender === 'male' ? 'men' : 'women',
          boulderIds: allBoulderIds,
        };
        const rows: AthleteRankRow[] = athletes.map(athlete => {
          const scores: Record<BoulderId, number> = {};
          let total = 0;
          allBoulderIds.forEach(bid => {
            const r = data.results[resultKey(athlete.id, bid)];
            const s = r ? scoreBoulder(r) : 0;
            scores[bid] = s;
            total += s;
          });
          return { athlete, scores, total, rank: 0 };
        });
        rows.sort((a, b) => b.total - a.total);
        rows.forEach((r, i) => (r.rank = i + 1));
        result.push({ cls: virtualCls, rows });
      });
  });

  return result.sort((a, b) => a.cls.name.localeCompare(b.cls.name));
}
